import React, { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useI18n } from '../i18n';
import RequestItemEditor from '../components/RequestItemEditor';
import { IconChevron } from '../components/PortalIcons';
import { fmtMoney } from '../lib/format';
import DocumentList from '../components/DocumentList';

// The forms behind the Commercial Accounts portal: a property, a unit, a
// colleague, a work order, and the spreadsheet import.
//
// Full screens rather than entries in Modals.js. That file is already 1000
// lines holding eighteen modals, and these are long forms — the property alone
// has four groups of fields — which is exactly the case a bottom sheet handles
// worst on a phone.

const NavBar = ({ title, onBack, onSave, saveLabel, busy }) => {
  const { t } = useI18n();
  return (
    <nav className="nav">
      <button className="btn btn-ghost btn-sm" onClick={onBack}>{t('nav.back')}</button>
      <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, letterSpacing: '.04em' }}>{title}</span>
      {onSave
        ? <button className="btn btn-primary btn-sm" onClick={onSave} disabled={busy}>{busy ? t('acct.saving') : (saveLabel || t('acct.save'))}</button>
        : <span style={{ width: 52 }} />}
    </nav>
  );
};

// A group of fields that starts closed. The property form asks about thirty
// things and only the address is required; opening all of it at once reads as
// a form nobody wants to fill in, when in practice they fill in four fields and
// come back to the rest when a tech asks where the shutoff is.
const Group = ({ title, children, open, onToggle, hint }) => (
  <div style={{ borderBottom: '1px solid var(--border)' }}>
    <button
      onClick={onToggle}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, padding: '16px 0', background: 'none', border: 'none', cursor: 'pointer',
        fontFamily: "'Cormorant Garamond', serif", fontSize: 17, color: 'var(--navy)', textAlign: 'left',
      }}
    >
      <span>
        {title}
        {hint && <span style={{ display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{hint}</span>}
      </span>
      <span style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .18s', color: 'var(--muted)', flexShrink: 0 }}>
        <IconChevron />
      </span>
    </button>
    {open && <div style={{ paddingBottom: 18 }}>{children}</div>}
  </div>
);

const PROPERTY_TYPES = ['single-family', 'condo', 'townhome', 'duplex', 'multiplex', 'apartment', 'commercial', 'other'];

// ── Add / edit a property ────────────────────────────────────────────────
export function PropertyForm() {
  const { currentScreen, propertyForm, setPropertyForm, saveProperty, closePropertyForm, formBusy, formError } = useApp();
  const { t } = useI18n();
  const [open, setOpen] = useState('basics');
  const f = propertyForm || {};
  const set = (patch) => setPropertyForm({ ...f, ...patch });
  const setIn = (group, patch) => setPropertyForm({ ...f, [group]: { ...(f[group] || {}), ...patch } });

  return (
    <div className={`screen ${currentScreen === 'screen-property-form' ? 'active' : ''}`}>
      <NavBar
        title={f._id ? t('acct.editProperty') : t('acct.addProperty')}
        onBack={closePropertyForm}
        onSave={saveProperty}
        busy={formBusy}
      />
      <div className="tp-body" style={{ padding: '4px 16px 40px' }}>
        {formError && <div className="alert alert-red" style={{ margin: '14px 0' }}>{formError}</div>}

        <Group title={t('acct.theBasics')} open={open === 'basics'} onToggle={() => setOpen(open === 'basics' ? '' : 'basics')}>
          <div className="form-group">
            <label>{t('acct.address')} <span className="req">*</span></label>
            <input type="text" value={f.addressLine1 || ''} onChange={(e) => set({ addressLine1: e.target.value })} placeholder="400 W Main St" />
          </div>
          <div className="form-row" style={{ gridTemplateColumns: '2fr 1fr 1.2fr' }}>
            <div className="form-group"><label>{t('req.city')}</label><input type="text" value={f.city || ''} onChange={(e) => set({ city: e.target.value })} /></div>
            <div className="form-group"><label>{t('req.state')}</label><input type="text" maxLength={2} autoCapitalize="characters" value={f.state || ''} onChange={(e) => set({ state: e.target.value.toUpperCase() })} /></div>
            <div className="form-group"><label>{t('req.zip')}</label><input type="text" inputMode="numeric" value={f.zip || ''} onChange={(e) => set({ zip: e.target.value })} /></div>
          </div>
          <div className="form-group">
            <label>{t('acct.propertyType')}</label>
            <select value={f.propertyType || 'single-family'} onChange={(e) => set({ propertyType: e.target.value })}>
              {PROPERTY_TYPES.map((v) => <option key={v} value={v}>{t(`acct.type.${v}`, v)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('acct.label')}</label>
            <input type="text" value={f.label || ''} onChange={(e) => set({ label: e.target.value })} placeholder="Cedar Park 4-plex" />
          </div>
          <div className="form-group">
            <label>{t('acct.externalId')}</label>
            <input type="text" value={f.externalId || ''} onChange={(e) => set({ externalId: e.target.value })} placeholder="P-100" />
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>{t('acct.externalIdHelp')}</div>
          </div>
          <div className="form-group">
            <label>{t('acct.nteAmount')}</label>
            <input type="number" inputMode="decimal" min="0" value={f.nteAmount ?? ''} onChange={(e) => set({ nteAmount: e.target.value })} placeholder={t('acct.usesCompanyDefault')} />
          </div>
        </Group>

        <Group title={t('acct.accessSite')} hint={t('acct.accessHint')} open={open === 'access'} onToggle={() => setOpen(open === 'access' ? '' : 'access')}>
          <div className="form-row">
            <div className="form-group"><label>{t('acct.gateCode')}</label><input type="text" value={f.access?.gateCode || ''} onChange={(e) => setIn('access', { gateCode: e.target.value })} /></div>
            <div className="form-group"><label>{t('acct.lockboxCode')}</label><input type="text" value={f.access?.lockboxCode || ''} onChange={(e) => setIn('access', { lockboxCode: e.target.value })} /></div>
          </div>
          <div className="form-group"><label>{t('acct.pets')}</label><input type="text" value={f.access?.pets || ''} onChange={(e) => setIn('access', { pets: e.target.value })} placeholder={t('acct.petsPlaceholder')} /></div>
          <div className="form-group"><label>{t('acct.parking')}</label><input type="text" value={f.access?.parkingNotes || ''} onChange={(e) => setIn('access', { parkingNotes: e.target.value })} /></div>
          <div className="form-group"><label>{t('acct.alarm')}</label><input type="text" value={f.access?.alarmInfo || ''} onChange={(e) => setIn('access', { alarmInfo: e.target.value })} /></div>
          <div className="form-group"><label>{t('acct.hoa')}</label><input type="text" value={f.access?.hoaName || ''} onChange={(e) => setIn('access', { hoaName: e.target.value })} /></div>
          <div className="form-group"><label>{t('acct.waterShutoff')}</label><input type="text" value={f.access?.waterShutoff || ''} onChange={(e) => setIn('access', { waterShutoff: e.target.value })} placeholder={t('acct.waterShutoffPlaceholder')} /></div>
          <div className="form-group"><label>{t('acct.breakerPanel')}</label><input type="text" value={f.access?.breakerPanel || ''} onChange={(e) => setIn('access', { breakerPanel: e.target.value })} /></div>
          <label className="same-check" style={{ alignItems: 'flex-start', lineHeight: 1.55 }}>
            <input type="checkbox" checked={!!f.access?.permissionToEnterDefault} onChange={(e) => setIn('access', { permissionToEnterDefault: e.target.checked })} style={{ marginTop: 3 }} />
            <span style={{ fontSize: 13 }}>{t('acct.permissionDefault')}</span>
          </label>
        </Group>

        <Group title={t('acct.buildingDetails')} hint={t('acct.buildingHint')} open={open === 'building'} onToggle={() => setOpen(open === 'building' ? '' : 'building')}>
          <div className="form-row">
            <div className="form-group"><label>{t('acct.beds')}</label><input type="number" inputMode="numeric" value={f.building?.beds ?? ''} onChange={(e) => setIn('building', { beds: e.target.value })} /></div>
            <div className="form-group"><label>{t('acct.baths')}</label><input type="number" inputMode="decimal" step="0.5" value={f.building?.baths ?? ''} onChange={(e) => setIn('building', { baths: e.target.value })} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>{t('acct.sqft')}</label><input type="number" inputMode="numeric" value={f.building?.sqft ?? ''} onChange={(e) => setIn('building', { sqft: e.target.value })} /></div>
            <div className="form-group"><label>{t('acct.yearBuilt')}</label><input type="number" inputMode="numeric" value={f.building?.yearBuilt ?? ''} onChange={(e) => setIn('building', { yearBuilt: e.target.value })} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>{t('acct.furnaceType')}</label><input type="text" value={f.building?.furnaceType || ''} onChange={(e) => setIn('building', { furnaceType: e.target.value })} /></div>
            <div className="form-group"><label>{t('acct.furnaceAge')}</label><input type="number" inputMode="numeric" value={f.building?.furnaceAge ?? ''} onChange={(e) => setIn('building', { furnaceAge: e.target.value })} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>{t('acct.acType')}</label><input type="text" value={f.building?.acType || ''} onChange={(e) => setIn('building', { acType: e.target.value })} /></div>
            <div className="form-group"><label>{t('acct.acAge')}</label><input type="number" inputMode="numeric" value={f.building?.acAge ?? ''} onChange={(e) => setIn('building', { acAge: e.target.value })} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>{t('acct.waterHeater')}</label><input type="text" value={f.building?.waterHeater || ''} onChange={(e) => setIn('building', { waterHeater: e.target.value })} /></div>
            <div className="form-group"><label>{t('acct.roofAge')}</label><input type="number" inputMode="numeric" value={f.building?.roofAge ?? ''} onChange={(e) => setIn('building', { roofAge: e.target.value })} /></div>
          </div>
          <div className="form-group"><label>{t('acct.notes')}</label><textarea rows="3" value={f.building?.notes || ''} onChange={(e) => setIn('building', { notes: e.target.value })} /></div>
        </Group>

        <Group title={t('acct.ownerBilling')} hint={t('acct.ownerHint')} open={open === 'owner'} onToggle={() => setOpen(open === 'owner' ? '' : 'owner')}>
          <div className="form-group"><label>{t('acct.ownerName')}</label><input type="text" value={f.owner?.name || ''} onChange={(e) => setIn('owner', { name: e.target.value })} /></div>
          <div className="form-row">
            <div className="form-group"><label>{t('acct.ownerPhone')}</label><input type="tel" value={f.owner?.phone || ''} onChange={(e) => setIn('owner', { phone: e.target.value })} /></div>
            <div className="form-group"><label>{t('acct.ownerEmail')}</label><input type="email" value={f.owner?.email || ''} onChange={(e) => setIn('owner', { email: e.target.value })} /></div>
          </div>
          <div className="form-group">
            <label>{t('acct.billingCode')}</label>
            <input type="text" value={f.owner?.billingCode || ''} onChange={(e) => setIn('owner', { billingCode: e.target.value })} />
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>{t('acct.billingCodeHelp')}</div>
          </div>
        </Group>
      </div>
    </div>
  );
}

// ── Add / edit a unit ────────────────────────────────────────────────────
export function UnitForm() {
  const { currentScreen, unitForm, setUnitForm, saveUnit, deleteUnit, closeUnitForm, formBusy, formError } = useApp();
  const { t } = useI18n();
  const u = unitForm || {};
  const set = (patch) => setUnitForm({ ...u, ...patch });

  return (
    <div className={`screen ${currentScreen === 'screen-unit-form' ? 'active' : ''}`}>
      <NavBar title={u._id ? t('acct.editUnit') : t('acct.addUnit')} onBack={closeUnitForm} onSave={saveUnit} busy={formBusy} />
      <div className="tp-body" style={{ padding: '18px 16px 40px' }}>
        {formError && <div className="alert alert-red" style={{ marginBottom: 14 }}>{formError}</div>}

        <div className="form-group">
          <label>{t('acct.unitNumber')} <span className="req">*</span></label>
          <input type="text" value={u.unitNumber || ''} onChange={(e) => set({ unitNumber: e.target.value })} placeholder="101" />
        </div>
        <div className="form-group">
          <label>{t('acct.occupancy')}</label>
          <select value={u.occupancy || 'occupied'} onChange={(e) => set({ occupancy: e.target.value })}>
            {['occupied', 'vacant', 'turnover'].map((v) => <option key={v} value={v}>{t(`acct.occupancy.${v}`)}</option>)}
          </select>
        </div>

        <div className="sec-label" style={{ marginTop: 18 }}>{t('acct.tenant')}</div>
        {/* The tenant is who the tech calls. Going through the management office
            to reach them is the single biggest cause of a wasted trip. */}
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 }}>{t('acct.tenantHelp')}</div>
        <div className="form-group"><label>{t('acct.tenantName')}</label><input type="text" value={u.tenantName || ''} onChange={(e) => set({ tenantName: e.target.value })} /></div>
        <div className="form-row">
          <div className="form-group"><label>{t('acct.tenantPhone')}</label><input type="tel" value={u.tenantPhone || ''} onChange={(e) => set({ tenantPhone: e.target.value })} /></div>
          <div className="form-group"><label>{t('acct.tenantEmail')}</label><input type="email" value={u.tenantEmail || ''} onChange={(e) => set({ tenantEmail: e.target.value })} /></div>
        </div>

        <div className="form-row">
          <div className="form-group"><label>{t('acct.beds')}</label><input type="number" inputMode="numeric" value={u.beds ?? ''} onChange={(e) => set({ beds: e.target.value })} /></div>
          <div className="form-group"><label>{t('acct.baths')}</label><input type="number" inputMode="decimal" step="0.5" value={u.baths ?? ''} onChange={(e) => set({ baths: e.target.value })} /></div>
        </div>
        <div className="form-group"><label>{t('acct.notes')}</label><textarea rows="3" value={u.notes || ''} onChange={(e) => set({ notes: e.target.value })} /></div>

        {u._id && (
          <button className="btn btn-ghost" style={{ marginTop: 18, color: 'var(--red)' }} onClick={deleteUnit}>{t('acct.removeUnit')}</button>
        )}
      </div>
    </div>
  );
}

// ── Invite / edit a colleague ────────────────────────────────────────────
export function MemberForm() {
  const { currentScreen, memberForm, setMemberForm, saveMember, removeMember, closeMemberForm, formBusy, formError } = useApp();
  const { t } = useI18n();
  const m = memberForm || {};
  const existing = Boolean(m.userId);
  const set = (patch) => setMemberForm({ ...m, ...patch });

  return (
    <div className={`screen ${currentScreen === 'screen-member-form' ? 'active' : ''}`}>
      <NavBar
        title={existing ? t('acct.editMember') : t('acct.invite')}
        onBack={closeMemberForm}
        onSave={saveMember}
        saveLabel={existing ? t('acct.save') : t('acct.sendInvite')}
        busy={formBusy}
      />
      <div className="tp-body" style={{ padding: '18px 16px 40px' }}>
        {formError && <div className="alert alert-red" style={{ marginBottom: 14 }}>{formError}</div>}

        {!existing && (
          <>
            <div className="form-group"><label>{t('req.fullName')} <span className="req">*</span></label><input type="text" value={m.name || ''} onChange={(e) => set({ name: e.target.value })} /></div>
            <div className="form-group"><label>{t('req.email')} <span className="req">*</span></label><input type="email" value={m.email || ''} onChange={(e) => set({ email: e.target.value })} /></div>
            <div className="form-group"><label>{t('req.phone')}</label><input type="tel" value={m.phone || ''} onChange={(e) => set({ phone: e.target.value })} /></div>
          </>
        )}
        {existing && (
          <div className="vsheet" style={{ padding: '0 0 14px' }}>
            <div className="vsheet-row"><div className="vsheet-label">{t('req.fullName')}</div><div className="vsheet-value">{m.name}</div></div>
            <div className="vsheet-row"><div className="vsheet-label">{t('req.email')}</div><div className="vsheet-value">{m.email}</div></div>
          </div>
        )}

        <div className="form-group" style={{ marginTop: 14 }}>
          <label>{t('acct.accessLevel')}</label>
          {/* Radio rows rather than a select, because the difference between
              these three is the whole point and a dropdown hides two thirds of
              it behind a tap. */}
          {['owner', 'manager', 'coordinator'].map((lv) => (
            <label key={lv} className="same-check" style={{ alignItems: 'flex-start', lineHeight: 1.5, marginBottom: 10 }}>
              <input type="radio" name="level" checked={(m.level || 'coordinator') === lv} onChange={() => set({ level: lv })} style={{ marginTop: 3 }} />
              <span>
                <span style={{ display: 'block', fontSize: 14, color: 'var(--navy)' }}>{t(`acct.level.${lv}`)}</span>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>{t(`acct.levelDesc.${lv}`)}</span>
              </span>
            </label>
          ))}
        </div>

        {!existing && (
          <div className="alert alert-blue" style={{ fontSize: 13 }}>{t('acct.inviteExplained')}</div>
        )}

        {existing && (
          <button className="btn btn-ghost" style={{ marginTop: 22, color: 'var(--red)' }} onClick={() => removeMember(m)}>
            {t('acct.removeMember')}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Raise a work order ───────────────────────────────────────────────────
//
// Starts with WHICH property, because that is the one thing the app already
// knows and the person filling this in should never have to type twice.
export function AccountWorkOrderForm() {
  const {
    currentScreen, account, properties, propertySearch, setPropertySearch, loadProperties,
    woForm, setWoForm, submitAccountWorkOrder, closeAccountWorkOrder,
    formBusy, formError, uploadFiles, serviceTypes, addWorkOrderDocuments, accountTechs,
  } = useApp();
  const { t } = useI18n();
  const w = woForm || {};
  const set = (patch) => setWoForm({ ...w, ...patch });
  const [editingItem, setEditingItem] = useState(null);

  const property = w.property;
  const units = useMemo(
    () => (property?.units || []).filter((u) => u.isActive !== false),
    [property]
  );

  // What limit this job will carry, worked out the same way the server does it,
  // so the number on screen is the number that gets saved.
  const effectiveNte = w.nteAmount !== '' && w.nteAmount != null ? Number(w.nteAmount)
    : property?.nteAmount != null ? property.nteAmount
    : account?.defaultNteAmount != null ? account.defaultNteAmount
    : null;
  const nteSource = w.nteAmount !== '' && w.nteAmount != null ? 'work-order'
    : property?.nteAmount != null ? 'property'
    : account?.defaultNteAmount != null ? 'account-default'
    : null;

  const items = w.items || [];

  if (editingItem !== null && items[editingItem]) {
    return (
      <div className={`screen ${currentScreen === 'screen-account-wo' ? 'active' : ''}`}>
        <RequestItemEditor
          item={items[editingItem]}
          index={editingItem}
          total={items.length}
          serviceTypes={serviceTypes}
          onChange={(next) => set({ items: items.map((it, i) => (i === editingItem ? next : it)) })}
          onDelete={() => { set({ items: items.filter((_, i) => i !== editingItem) }); setEditingItem(null); }}
          onClose={() => setEditingItem(null)}
          onUploadFiles={uploadFiles}
        />
      </div>
    );
  }

  return (
    <div className={`screen ${currentScreen === 'screen-account-wo' ? 'active' : ''}`}>
      <NavBar
        title={t('acct.newServiceRequest')}
        onBack={closeAccountWorkOrder}
        onSave={submitAccountWorkOrder}
        saveLabel={t('acct.submit')}
        busy={formBusy}
      />
      <div className="tp-body" style={{ padding: '18px 16px 40px' }}>
        {formError && <div className="alert alert-red" style={{ marginBottom: 14 }}>{formError}</div>}

        {/* ── Which property ── */}
        <div className="sec-label">{t('acct.whichProperty')}</div>
        {property ? (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 15, color: 'var(--navy)' }}>{property.addressLine1}</div>
            <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>
              {[property.label, property.city, property.zip].filter(Boolean).join(' · ')}
            </div>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => set({ property: null, unitId: '' })}>
              {t('acct.chooseDifferent')}
            </button>
          </div>
        ) : (
          <>
            <div className="form-group">
              <input
                type="search"
                value={propertySearch}
                onChange={(e) => { setPropertySearch(e.target.value); loadProperties(e.target.value); }}
                placeholder={t('acct.searchProperties')}
              />
            </div>
            <div className="tp-rows" style={{ marginBottom: 16, maxHeight: 320, overflowY: 'auto' }}>
              {(properties || []).slice(0, 40).map((p) => (
                <button key={p._id} className="tp-row" onClick={() => set({ property: p, unitId: '' })}>
                  <div className="tp-row-main">
                    <div className="tp-row-name">{p.addressLine1}</div>
                    <div className="tp-row-meta">{[p.label, p.city, p.zip].filter(Boolean).join(' · ')}</div>
                  </div>
                  <IconChevron />
                </button>
              ))}
            </div>
          </>
        )}

        {property && (
          <>
            {units.length > 0 && (
              <div className="form-group">
                <label>{t('acct.whichUnit')}</label>
                <select value={w.unitId || ''} onChange={(e) => set({ unitId: e.target.value })}>
                  <option value="">{t('acct.wholeProperty')}</option>
                  {units.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.unitNumber ? `#${u.unitNumber}` : t('acct.wholeProperty')}
                      {u.tenantName ? ` — ${u.tenantName}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* ── What needs doing ── */}
            <div className="sec-label" style={{ marginTop: 20 }}>{t('acct.whatNeedsDoing')}</div>
            {items.map((it, i) => (
              <button key={i} className="tp-row" onClick={() => setEditingItem(i)}>
                <div className="tp-row-main">
                  <div className="tp-row-name">{it.title || it.serviceType || t('acct.untitledJob')}</div>
                  <div className="tp-row-meta">{it.description || t('acct.tapToDescribe')}</div>
                </div>
                <IconChevron />
              </button>
            ))}
            <button
              className="btn btn-outline"
              style={{ marginTop: 10 }}
              onClick={() => { set({ items: [...items, { title: '', serviceType: '', description: '', photos: [], files: [] }] }); setEditingItem(items.length); }}
            >
              {items.length ? t('acct.addAnotherJob') : t('acct.addJob')}
            </button>

            {/* ── Paperwork ──
                The move-out inspection the turn is being quoted against. It
                arrives by email today and stops there; attached here it reaches
                the tech who is actually standing in the unit. */}
            <div style={{ marginTop: 24 }}>
              <DocumentList
                title={t('doc.attachTitle')}
                documents={w.documents || []}
                onAdd={addWorkOrderDocuments}
                emptyText={t('doc.attachHelp')}
              />
            </div>

            {/* ── Priority ── */}
            <div className="sec-label" style={{ marginTop: 24 }}>{t('acct.priority')}</div>
            {['routine', 'urgent', 'emergency'].map((lv) => (
              <label key={lv} className="same-check" style={{ alignItems: 'flex-start', lineHeight: 1.5, marginBottom: 10 }}>
                <input type="radio" name="priority" checked={(w.priority || 'routine') === lv} onChange={() => set({ priority: lv })} style={{ marginTop: 3 }} />
                <span>
                  <span style={{ display: 'block', fontSize: 14, color: lv === 'emergency' ? 'var(--red)' : 'var(--navy)' }}>{t(`acct.priority.${lv}`)}</span>
                  <span style={{ display: 'block', fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>{t(`acct.priorityDesc.${lv}`)}</span>
                </span>
              </label>
            ))}

            {/* ── Who they would like ──
                A company that has had the same man in their buildings for a
                year has a real reason to want him again, and a preference lost
                in a phone call is how they end up ringing him directly. It is a
                REQUEST: we still dispatch, because they cannot see who is
                already booked out on Tuesday. */}
            {accountTechs?.length > 0 && (
              <>
                <div className="sec-label" style={{ marginTop: 24 }}>{t('acct.preferredTech')}</div>
                <div className="form-group">
                  <select value={w.requestedVendorId || ''} onChange={(e) => set({ requestedVendorId: e.target.value })}>
                    <option value="">{t('acct.anyTech')}</option>
                    {accountTechs.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}{v.trades?.length ? ` — ${v.trades.join(', ')}` : ''}</option>
                    ))}
                  </select>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>{t('acct.preferredTechHelp')}</div>
                </div>
              </>
            )}

            {/* ── Spending limit ── */}
            <div className="sec-label" style={{ marginTop: 24 }}>{t('acct.spendLimit')}</div>
            <div className="form-group">
              <input
                type="number" inputMode="decimal" min="0"
                value={w.nteAmount ?? ''}
                onChange={(e) => set({ nteAmount: e.target.value })}
                placeholder={effectiveNte != null ? String(effectiveNte) : t('acct.noLimitSet')}
              />
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
                {effectiveNte != null
                  ? t('acct.nteFrom')
                      .replace('{amount}', fmtMoney(effectiveNte))
                      .replace('{source}', t(`acct.nteSource.${nteSource}`))
                  : t('acct.nteNoneHelp')}
              </div>
            </div>

            {/* ── Getting in ── */}
            <div className="sec-label" style={{ marginTop: 24 }}>{t('acct.gettingIn')}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 }}>{t('acct.gettingInHelp')}</div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('acct.tenantName')}</label>
                <input type="text" value={w.access?.tenantName ?? ''} onChange={(e) => set({ access: { ...(w.access || {}), tenantName: e.target.value } })} placeholder={w.tenantHint?.name || ''} />
              </div>
              <div className="form-group">
                <label>{t('acct.tenantPhone')}</label>
                <input type="tel" value={w.access?.tenantPhone ?? ''} onChange={(e) => set({ access: { ...(w.access || {}), tenantPhone: e.target.value } })} placeholder={w.tenantHint?.phone || ''} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('acct.lockboxCode')}</label>
                <input type="text" value={w.access?.lockboxCode ?? ''} onChange={(e) => set({ access: { ...(w.access || {}), lockboxCode: e.target.value } })} placeholder={property.access?.lockboxCode || ''} />
              </div>
              <div className="form-group">
                <label>{t('acct.gateCode')}</label>
                <input type="text" value={w.access?.gateCode ?? ''} onChange={(e) => set({ access: { ...(w.access || {}), gateCode: e.target.value } })} placeholder={property.access?.gateCode || ''} />
              </div>
            </div>
            <div className="form-group">
              <label>{t('acct.accessNotes')}</label>
              <textarea rows="2" value={w.access?.notes ?? ''} onChange={(e) => set({ access: { ...(w.access || {}), notes: e.target.value } })} placeholder={t('acct.accessNotesPlaceholder')} />
            </div>
            <label className="same-check" style={{ alignItems: 'flex-start', lineHeight: 1.55 }}>
              <input
                type="checkbox"
                checked={w.access?.permissionToEnter ?? Boolean(property.access?.permissionToEnterDefault)}
                onChange={(e) => set({ access: { ...(w.access || {}), permissionToEnter: e.target.checked } })}
                style={{ marginTop: 3 }}
              />
              <span style={{ fontSize: 13 }}>{t('acct.permissionToEnter')}</span>
            </label>
          </>
        )}
      </div>
    </div>
  );
}

// ── Import a spreadsheet ─────────────────────────────────────────────────
//
// Pick a file, check how the columns were read, then commit. The middle step is
// the whole point: guessing silently and writing four hundred wrong rows takes
// far longer to unpick than it took to create.
export function PropertyImport() {
  const {
    currentScreen, importState, setImportState, previewImport, commitImport,
    closePropertyImport, formBusy, formError,
  } = useApp();
  const { t } = useI18n();
  const s = importState || {};

  const readFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => previewImport(String(reader.result || ''), file.name);
    // Read in the browser and post the TEXT, so there is no upload endpoint,
    // no multer, and no half-uploaded file to reason about.
    reader.readAsText(file);
  };

  const FIELDS = [
    'externalId', 'label', 'addressLine1', 'addressLine2', 'unitNumber', 'city', 'state', 'zip',
    'propertyType', 'beds', 'baths', 'sqft', 'yearBuilt',
    'tenantName', 'tenantPhone', 'tenantEmail',
    'ownerName', 'ownerPhone', 'ownerEmail', 'billingCode',
    'gateCode', 'lockboxCode', 'pets', 'parkingNotes', 'hoaName', 'notes',
  ];

  return (
    <div className={`screen ${currentScreen === 'screen-property-import' ? 'active' : ''}`}>
      <NavBar
        title={t('acct.import')}
        onBack={closePropertyImport}
        onSave={s.preview && !s.result ? commitImport : null}
        saveLabel={t('acct.importNow')}
        busy={formBusy}
      />
      <div className="tp-body" style={{ padding: '18px 16px 40px' }}>
        {formError && <div className="alert alert-red" style={{ marginBottom: 14 }}>{formError}</div>}

        {/* Step 1 — the file */}
        {!s.preview && !s.result && (
          <>
            <div className="section-title">{t('acct.importTitle')}</div>
            <p style={{ fontSize: 13.5, color: 'var(--slate)', lineHeight: 1.6, margin: '8px 0 20px' }}>
              {t('acct.importIntro')}
            </p>
            <label className="btn btn-primary btn-lg" style={{ display: 'block', textAlign: 'center', cursor: 'pointer' }}>
              {t('acct.chooseFile')}
              {/* Visually hidden rather than display:none — Safari will not open
                  a file picker for an input it considers not displayed. */}
              <input
                type="file" accept=".csv,text/csv,text/plain"
                onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ''; }}
                style={{ position: 'absolute', opacity: 0, width: 1, height: 1, clip: 'rect(0 0 0 0)' }}
              />
            </label>
            <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 16, lineHeight: 1.6 }}>
              {t('acct.importHint')}
            </div>
          </>
        )}

        {/* Step 2 — check the columns */}
        {s.preview && !s.result && (
          <>
            <div className="alert alert-blue" style={{ marginBottom: 18 }}>
              {t('acct.importSummary')
                .replace('{file}', s.fileName || 'CSV')
                .replace('{rows}', s.preview.rowCount)
                .replace('{props}', s.preview.total)}
            </div>

            {s.preview.duplicates > 0 && (
              <div className="alert alert-amber" style={{ marginBottom: 14 }}>
                {t('acct.importDuplicates').replace('{n}', s.preview.duplicates)}
              </div>
            )}
            {s.preview.failedCount > 0 && (
              <div className="alert alert-amber" style={{ marginBottom: 14 }}>
                {t('acct.importSkipped').replace('{n}', s.preview.failedCount)}
                <ul style={{ margin: '8px 0 0 18px', fontSize: 12.5 }}>
                  {s.preview.failed.slice(0, 5).map((f) => (
                    <li key={f.line}>{t('acct.importRow').replace('{n}', f.line)} — {f.errors.join(', ')}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="sec-label" style={{ marginTop: 8 }}>{t('acct.columnMapping')}</div>
            <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.55 }}>
              {t('acct.columnMappingHelp')}
            </div>
            {FIELDS.map((field) => (
              <div className="form-group" key={field} style={{ marginBottom: 10 }}>
                <label>{t(`acct.field.${field}`, field)}</label>
                <select
                  value={s.preview.mapping[field] || ''}
                  onChange={(e) => {
                    const mapping = { ...s.preview.mapping };
                    if (e.target.value) mapping[field] = e.target.value; else delete mapping[field];
                    setImportState({ ...s, preview: { ...s.preview, mapping } });
                  }}
                >
                  <option value="">{t('acct.notImported')}</option>
                  {s.preview.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            ))}

            <button className="btn btn-outline" style={{ marginTop: 6 }} onClick={() => previewImport(s.text, s.fileName, s.preview.mapping)}>
              {t('acct.recheck')}
            </button>

            <div className="sec-label" style={{ marginTop: 24 }}>{t('acct.previewRows')}</div>
            <div className="tp-rows">
              {(s.preview.sample || []).map((p, i) => (
                <div key={i} className="tp-row" style={{ cursor: 'default' }}>
                  <div className="tp-row-main">
                    <div className="tp-row-name">{p.addressLine1}</div>
                    <div className="tp-row-meta">
                      {[p.city, p.zip, p.units?.length ? t('acct.unitCount').replace('{n}', p.units.length) : ''].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Step 3 — what happened */}
        {s.result && (
          <>
            <div className="section-title">{t('acct.importDone')}</div>
            <div className="stat-grid" style={{ margin: '16px 0' }}>
              <div className="stat-card"><div className="stat-v">{s.result.created}</div><div className="stat-l">{t('acct.added')}</div></div>
              <div className="stat-card"><div className="stat-v">{s.result.updated}</div><div className="stat-l">{t('acct.updated')}</div></div>
              <div className="stat-card"><div className="stat-v">{s.result.skipped}</div><div className="stat-l">{t('acct.skipped')}</div></div>
            </div>
            {(s.result.failed || []).length > 0 && (
              <div className="alert alert-amber">
                {t('acct.importSkippedDetail')}
                <ul style={{ margin: '8px 0 0 18px', fontSize: 12.5 }}>
                  {s.result.failed.slice(0, 20).map((f) => (
                    <li key={f.line}>{t('acct.importRow').replace('{n}', f.line)} — {f.errors.join(', ')}</li>
                  ))}
                </ul>
              </div>
            )}
            <button className="btn btn-primary btn-lg" style={{ marginTop: 18 }} onClick={closePropertyImport}>
              {t('acct.backToProperties')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
